import { forwardRef } from 'react';
import {
  Link,
  NavLink,
  useNavigate,
  type LinkProps,
  type NavLinkProps,
  type NavigateOptions,
} from 'react-router';
import { useLocale } from './locale-context';
import { localePath } from './locale-path';

/**
 * Drop-in replacement for react-router's <Link>. When `to` is a string in-app
 * path, it is prefixed with the active locale (`/uz/…` for uz, bare for ru) so
 * navigation stays within the current language. Non-string `to` (location
 * objects) and external URLs are passed through untouched.
 */
export const LocaleLink = forwardRef<HTMLAnchorElement, LinkProps>(function LocaleLink(
  { to, ...rest },
  ref,
) {
  const { locale } = useLocale();
  const next = typeof to === 'string' && to.startsWith('/') ? localePath(to, locale) : to;
  return <Link ref={ref} to={next} {...rest} />;
});

/**
 * Drop-in replacement for react-router's <NavLink>. Same locale-prefixing
 * behavior as {@link LocaleLink}, but for the active-match nav variant used by
 * the sidebar's primary nav. The render-prop forms of `className`/`children`/
 * `style` (which receive `{ isActive, isPending, isTransitioning }`) and other
 * NavLink-only props (`end`, `caseSensitive`, …) are untouched — only `to` is
 * rewritten before delegating to NavLink.
 */
export const LocaleNavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function LocaleNavLink(
  { to, ...rest },
  ref,
) {
  const { locale } = useLocale();
  const next = typeof to === 'string' && to.startsWith('/') ? localePath(to, locale) : to;
  return <NavLink ref={ref} to={next} {...rest} />;
});

/**
 * `navigate` that locale-prefixes string in-app paths. Mirrors useNavigate's call
 * shape. Only absolute paths (starting with `/`) are prefixed — mirrors the
 * `to.startsWith('/')` guard in {@link LocaleLink}/{@link LocaleNavLink} above, so a
 * relative `to` (e.g. `'foo'`) is passed through untouched instead of being
 * mangled into `/rufoo`.
 */
export function useLocaleNavigate() {
  const navigate = useNavigate();
  const { locale } = useLocale();
  return (to: string, opts?: NavigateOptions) =>
    navigate(to.startsWith('/') ? localePath(to, locale) : to, opts);
}
